import { useMemo } from 'react';
import { Avatar, Typography } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useUserTypeRender } from './useUserTypeRender';
import { useUserStatusRender } from './useUserStatusRender';
import { useUserTimestampRender } from '@src/hooks';
import { useAvatarRender } from '@src/hooks/useAvatarRender';
import {
  USER_TYPE_FILTER_OPTIONS,
  USER_STATUS_FILTER_OPTIONS,
} from '../constants';

const { Text } = Typography;

export const useUsersColumns = (): SmartDataTableProps['columns'] => {
  const getUserTypeConfig = useUserTypeRender();
  const getUserStatusConfig = useUserStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });
  const avatarRender = useAvatarRender({
    size: 35,
    fontSize: '16px',
    useConsistentColors: true,
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 60,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'email',
        title: 'Email',
        dataIndex: 'email',
        sorter: false,
        ellipsis: true,
        render: (email: any) => (
          <Text style={{ color: '#1890ff' }}>{email || 'N/A'}</Text>
        ),
      },
      {
        key: 'user_info',
        title: 'User Info',
        dataIndex: 'first_name',
        sorter: false,
        render: (firstName: any, record: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Avatar Column */}
            <div style={{ flexShrink: 0 }}>{avatarRender(record)}</div>

            {/* Name and Mobile Column */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Name Row */}
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#262626',
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${record.first_name || ''} ${record.last_name || ''}`.trim() ||
                  'Unknown User'}
              </div>

              {/* Mobile Row */}
              <div
                style={{
                  fontSize: '12px',
                  color: '#8c8c8c',
                  fontFamily: 'monospace',
                }}
              >
                {record.mobile_no ? `+${record.mobile_no}` : 'No mobile'}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: 'type',
        title: 'User Type',
        dataIndex: 'type',
        sorter: true,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: USER_TYPE_FILTER_OPTIONS.map(opt => ({
          label: opt.text,
          value: opt.value,
        })),
        render: (type: any) => getUserTypeConfig(type),
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: USER_STATUS_FILTER_OPTIONS.map(opt => ({
          label: opt.text,
          value: opt.value,
        })),
        render: (status: any) => getUserStatusConfig(status),
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [getUserTypeConfig, getUserStatusConfig, userTimestampRender, avatarRender]
  );
};
